/**
 * Vendor market-snapshot quotes (dashboard hero). Independent of universe
 * filter and window: last vs previous close, not cap-weighted membership.
 *
 * Wire codes are the closed SNAPSHOT_CODES tuple. Nasdaq Composite is COMP;
 * NDX remains the Nasdaq-100 membership code (ADR-0032). The two are different
 * instruments, not two spellings of one — which is why both may coexist.
 * Yahoo spellings stay in qfinance provenance.
 */
import { z } from 'zod';

import { MarketDateSchema } from './primitives';

export const SNAPSHOT_CODES = [
  'SPX',
  'DJI',
  'COMP',
  'RUT',
  'VIX',
  'BTC',
  'ETH',
  'GOLD',
  'CL',
  'US10Y',
] as const;
export const SnapshotCodeSchema = z.enum(SNAPSHOT_CODES);
export type SnapshotCode = z.infer<typeof SnapshotCodeSchema>;

export const SNAPSHOT_CATEGORIES = ['index', 'volatility', 'crypto', 'commodity', 'bond'] as const;
export const SnapshotCategorySchema = z.enum(SNAPSHOT_CATEGORIES);
export type SnapshotCategory = z.infer<typeof SnapshotCategorySchema>;

export const SNAPSHOT_BANDS = ['featured', 'compact'] as const;
export const SnapshotBandSchema = z.enum(SNAPSHOT_BANDS);
export type SnapshotBand = z.infer<typeof SnapshotBandSchema>;

export interface SnapshotInstrument {
  readonly code: SnapshotCode;
  readonly name: string;
  readonly category: SnapshotCategory;
  readonly band: SnapshotBand;
  readonly onTape: boolean;
}

/**
 * Closed catalog. Tape / featured / compact layout is a contract, not a
 * CSS accident: local and HTTP must paint the same instruments in this order.
 */
/**
 * Keyed by code so exhaustiveness is a TYPE fact: adding a member to
 * SNAPSHOT_CODES without a catalog row fails to compile here, rather than
 * compiling and then silently dropping the instrument in
 * `buildMarketSnapshotItems` (which iterates the catalog, not the codes).
 */
const SNAPSHOT_CATALOG: Readonly<Record<SnapshotCode, SnapshotInstrument>> = {
  SPX: { code: 'SPX', name: 'S&P 500', category: 'index', band: 'featured', onTape: false },
  DJI: { code: 'DJI', name: 'Dow Jones', category: 'index', band: 'featured', onTape: true },
  COMP: {
    code: 'COMP',
    name: 'Nasdaq Composite',
    category: 'index',
    band: 'featured',
    onTape: true,
  },
  RUT: { code: 'RUT', name: 'Russell 2000', category: 'index', band: 'featured', onTape: true },
  VIX: { code: 'VIX', name: 'VIX', category: 'volatility', band: 'featured', onTape: true },
  BTC: { code: 'BTC', name: 'Bitcoin', category: 'crypto', band: 'compact', onTape: true },
  ETH: { code: 'ETH', name: 'Ethereum', category: 'crypto', band: 'compact', onTape: true },
  GOLD: { code: 'GOLD', name: 'Gold', category: 'commodity', band: 'compact', onTape: true },
  CL: { code: 'CL', name: 'Crude Oil', category: 'commodity', band: 'compact', onTape: false },
  US10Y: { code: 'US10Y', name: '10Y Treasury', category: 'bond', band: 'compact', onTape: false },
};

/** Catalog order IS the paint order — local and HTTP must agree on it. */
export const SNAPSHOT_INSTRUMENTS: readonly SnapshotInstrument[] = SNAPSHOT_CODES.map(
  (code) => SNAPSHOT_CATALOG[code],
);

export function isSnapshotCode(value: string): value is SnapshotCode {
  return (SNAPSHOT_CODES as readonly string[]).includes(value);
}

/**
 * Total on a closed key union — `Record<SnapshotCode, _>` admits no miss, so
 * there is no unresolved case to guard (style §4, unreachable invariants).
 */
export function snapshotInstrument(code: SnapshotCode): SnapshotInstrument {
  return SNAPSHOT_CATALOG[code];
}

export const MarketSnapshotItemSchema = z.object({
  code: SnapshotCodeSchema,
  name: z.string().min(1).max(32),
  category: SnapshotCategorySchema,
  last: z.number().positive(),
  /** Absolute move vs the previous close. `null` when there is no previous bar — never 0 (R2b). */
  change: z.number().nullable(),
  /** FRACTION, not percent: `change / previousClose`. Multiply by 100 to display. */
  changePct: z.number().nullable(),
  /** The date of THIS instrument's last bar, which may lag the payload's `asOf`. */
  asOn: MarketDateSchema,
});
export type MarketSnapshotItem = z.infer<typeof MarketSnapshotItemSchema>;

export const MarketSnapshotPayloadSchema = z.object({
  /** Published `data_as_of`; null in local/synthetic mode (no ingest). */
  asOf: MarketDateSchema.nullable(),
  items: z
    .array(MarketSnapshotItemSchema)
    .max(SNAPSHOT_CODES.length)
    .superRefine((val, ctx) => {
      if (new Set(val.map((row) => row.code)).size !== val.length) {
        ctx.addIssue({ code: 'custom', message: 'items codes must be unique' });
      }
    }),
});
export type MarketSnapshotPayload = z.infer<typeof MarketSnapshotPayloadSchema>;
